import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { clearPersonVitalsByPersonIdAction } from '@redux/person/personActions';
import { getPersonByIdSelector } from '@redux/person/personSelector';
import { deletePersonThunk } from '@redux/person/personThunks';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { ConfirmationDialogV2 } from '../ConfirmationDialog/ConfirmationDialogV2';
import { TrashIcon } from '../Icons';

export type DeletePatientProps = {
  patientId: string;
  visible: boolean;
  onClosePress: () => void;
  onConfirmPress?: () => void;
};

type PropsFromRedux = ConnectedProps<typeof connectRedux>;
type Props = PropsFromRedux & DeletePatientProps;

const DeletePatientModal: React.FC<Props> = ({ patientId, personByIdSelector, deletePatient, deletePatientFromTable, visible, onClosePress, onConfirmPress }) => {
  const { fullName = '' } = personByIdSelector[patientId] ?? {};
  const { showAlertInfo, showAlertWarning } = useAlert();

  const onDeletePatientConfirmation = async () => {
    onClosePress();
    try {
      await deletePatient(patientId);
      onConfirmPress?.();
      deletePatientFromTable(patientId);
      showAlertInfo({
        message: 'Patient was deactivated successfully!'
      });
    } catch (err) {
      showAlertWarning({
        message: 'Something went wrong while deactivating the patient. Please try again.'
      });
      console.log('Failed to deactivate patient.');
    }
  };

  return (
    <ConfirmationDialogV2
      open={visible}
      icon={TrashIcon}
      title={`Deactivate patient ${fullName}?`}
      description="Are you sure you want to deactivate this patient? This action cannot be undone."
      proceedEvent={onDeletePatientConfirmation}
      proceedEventText="Deactivate"
      closeEvent={onClosePress}
    />
  );
};

const connectRedux = connect(
  (_: ReduxState) => (state: ReduxState) => ({
    personByIdSelector: getPersonByIdSelector(state)
  }),
  {
    deletePatient: deletePersonThunk,
    deletePatientFromTable: clearPersonVitalsByPersonIdAction
  }
);

export default compose(connectRedux)(DeletePatientModal);
